import React from "react";
import { useHistory, useParams } from "react-router-dom";
import firebase from "firebase/compat/app";

// Material UI
import Grid from "@material-ui/core/Grid";

// Locals
import RaisableCard from "../../components/cards";
import BasicInfo from "./basicInfo";
import FieldsOptions from "./fieldsOptions";
import ArchivedStatusManagement from "./archivedStatusManagement";
import Statistics from "./statistics";
import RecentlyViewedItems from "./recentlyViewedItems";
import WarrantyEndingItems from "./warrantyEndingItems";
import useStyles from "../styles";
import { firestoreQuery, localStorageKey } from "../../constants";
import routes from "../../routes";
import ChartStatistics from "./chartStatistics";
import { useCurrentWsDetail, useOrdered } from "../../firebase";
import { prepareStatistics } from "./helpers";
import { useUserId } from "../../authentication/hooks";

// Types
import type { SingleItemState } from "../../items/types";
import type { WsParams } from "../../routes";

const Detail: React.FC = () => {
  const classes = useStyles();
  const { replace } = useHistory();
  const { workspaceId } = useParams<WsParams>();
  const userId = useUserId();
  const items = useOrdered<SingleItemState>(firestoreQuery.wsItems);
  const { statisticsToShow } = useCurrentWsDetail();

  React.useEffect(() => {
    if (workspaceId && userId) {
      if (!localStorage.getItem(localStorageKey.lastUsedWorkspace(userId))) {
        localStorage.setItem(localStorageKey.lastUsedWorkspace(userId), workspaceId);
      }
      firebase
        .auth()
        .currentUser?.getIdTokenResult()
        .then((userTokenData) => {
          if (!userTokenData.claims.adminForWS?.[workspaceId]) {
            console.log(userTokenData.claims);
            const userId = firebase.auth().currentUser?.uid;

            replace(routes.workspaceUserInfo(workspaceId, userId));
          }
        });
    }
  }, [replace, workspaceId, userId]);

  const [countData, chartData] = React.useMemo(() => {
    if (!(items && statisticsToShow)) return [{}, {}];
    return prepareStatistics(items, statisticsToShow);
  }, [items, statisticsToShow]);

  return (
    <Grid container spacing={2}>
      {/* First Column */}
      <Grid item xs={12} lg={5} style={{ display: "flex" }}>
        <Grid container spacing={2} direction="column">
          <Grid item style={{ flex: 1 }}>
            <RaisableCard className={classes.gridCard}>
              <div className={classes.cardPadding}>
                <BasicInfo />
              </div>
            </RaisableCard>
          </Grid>
          <Grid item style={{ flex: 0.5 }}>
            <RaisableCard className={classes.gridCard}>
              <div className={classes.cardPadding}>
                <ArchivedStatusManagement />
              </div>
            </RaisableCard>
          </Grid>
          <Grid item style={{ flex: 1 }}>
            <RaisableCard className={classes.gridCard}>
              <div className={classes.cardPadding}>
                <Statistics data={countData} allCount={items?.length || 0} />
              </div>
            </RaisableCard>
          </Grid>
        </Grid>
      </Grid>

      {/* Second and Third Columns */}
      <Grid item xs={12} lg={7} style={{ display: "flex" }}>
        <Grid container spacing={2}>
          {/* Second Column Content */}
          <Grid item xs={12} lg={4}>
            <RaisableCard className={classes.gridCard}>
              <div className={classes.cardPadding}>
                <RecentlyViewedItems />
              </div>
            </RaisableCard>
          </Grid>

          {/* Third Column Content */}
          <Grid item xs={12} lg={8}>
            <RaisableCard className={classes.gridCard}>
              <div className={classes.cardPadding}>
                <WarrantyEndingItems />
              </div>
            </RaisableCard>
          </Grid>

          {/* Fields Options spanning both second and third columns */}
          <Grid item xs={12}>
            <RaisableCard className={classes.gridCard}>
              <div className={classes.cardPadding}>
                <FieldsOptions />
              </div>
            </RaisableCard>
          </Grid>
        </Grid>
      </Grid>

      {/* Full Width Row */}
      <Grid item xs={12}>
        <RaisableCard>
          <div className={classes.cardPadding}>
            <ChartStatistics data={chartData} />
          </div>
        </RaisableCard>
      </Grid>
    </Grid>
  );
};

export default Detail;
